import React, { useState } from 'react';
import { MessageCircle, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../src/contexts/LanguageContext';
const PatientInteraction = () => {
  const {
    t
  } = useTranslation();
  const {
    language
  } = useLanguage();
  const [activeConversation, setActiveConversation] = useState('medication');
  // Conversations for different topics
  const conversations = {
    medication: {
      patient: t('patient.conversations.medication.patient'),
      nurse: t('patient.conversations.medication.nurse')
    },
    pain: {
      patient: t('patient.conversations.pain.patient'),
      nurse: t('patient.conversations.pain.nurse')
    },
    family: {
      patient: t('patient.conversations.family.patient'),
      nurse: t('patient.conversations.family.nurse')
    }
  };
  // Cultural adaptation - show different backgrounds based on language
  const getBackgroundImage = () => {
    switch (language) {
      case 'zh':
        return 'https://images.unsplash.com/photo-1614641096015-15c5b3c19f52?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80';
      case 'ms':
        return 'https://images.unsplash.com/photo-1596422846543-75c6fc197f11?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80';
      case 'ta':
        return 'https://images.unsplash.com/photo-1624461776250-5cd055c6c64c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80';
      case 'th':
        return 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80';
      default:
        return 'https://images.unsplash.com/photo-1581056771107-24ca5f033842?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80';
    }
  };
  return <section id="patient-interaction" className="py-16 bg-cream-white relative" data-id="element-691">
      <div className="absolute inset-0 bg-gradient-to-b from-sage-mint/5 to-cream-white" data-id="element-692"></div>
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `url(${getBackgroundImage()})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      mixBlendMode: 'overlay'
    }} data-id="element-693"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-694">
        <div className="text-center mb-12" data-id="element-695">
          <h2 className="text-2xl md:text-3xl font-light text-charcoal mb-4" data-id="element-696">
            {t('patient.title')}
          </h2>
          <p className="text-grey-600 max-w-2xl mx-auto" data-id="element-697">
            {t('patient.subtitle')}
          </p>
        </div>
        {/* Conversation topics */}
        <div className="flex justify-center mb-8 overflow-x-auto pb-2" data-id="element-698">
          <div className="flex space-x-2" data-id="element-699">
            {Object.keys(conversations).map(topic => <button key={topic} className={`px-5 py-2 rounded-full text-sm transition-all duration-300 whitespace-nowrap
                  ${activeConversation === topic ? 'bg-sage-mint text-white shadow-glow-mint' : 'bg-white text-grey-600 hover:bg-sage-mint/10 hover:scale-105'}`} onClick={() => setActiveConversation(topic)} data-id="element-700">
                {topic.charAt(0).toUpperCase() + topic.slice(1)}
              </button>)}
          </div>
        </div>
        {/* Conversation display */}
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-card overflow-hidden" data-id="element-701">
          <div className="p-6 border-b border-grey-400/20 flex items-center space-x-2" data-id="element-702">
            <MessageCircle className="h-5 w-5 text-sage-mint" data-id="element-703" />
            <h3 className="text-lg font-medium text-charcoal" data-id="element-704">
              {activeConversation.charAt(0).toUpperCase() + activeConversation.slice(1)}{' '}
              Conversation
            </h3>
          </div>
          <div className="p-6 space-y-6" data-id="element-705">
            {/* Patient message */}
            <div className="flex items-start" data-id="element-706">
              <div className="w-10 h-10 rounded-full bg-grey-400/20 flex-shrink-0 flex items-center justify-center" data-id="element-707">
                <span className="text-grey-600 font-medium" data-id="element-708">P</span>
              </div>
              <div className="ml-4 bg-grey-400/10 rounded-lg p-4 max-w-[80%]" data-id="element-709">
                <p className="text-grey-600" data-id="element-710">
                  {conversations[activeConversation].patient}
                </p>
              </div>
            </div>
            {/* Nurse message */}
            <div className="flex items-start justify-end" data-id="element-711">
              <div className="mr-4 bg-sage-mint/10 rounded-lg p-4 max-w-[80%]" data-id="element-712">
                <p className="text-grey-600" data-id="element-713">
                  {conversations[activeConversation].nurse}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-sage-mint/20 flex-shrink-0 flex items-center justify-center" data-id="element-714">
                <span className="text-sage-mint font-medium" data-id="element-715">N</span>
              </div>
            </div>
            {/* Cultural adaptation note */}
            <div className="mt-8 pt-4 border-t border-grey-400/20" data-id="element-716">
              <p className="text-xs text-grey-600 italic" data-id="element-717">
                <span className="text-sage-mint" data-id="element-718">♦</span> This conversation
                demonstrates cultural sensitivity and professional communication
                in{' '}
                {language === 'en' ? 'English' : language === 'zh' ? 'Chinese' : language === 'ms' ? 'Malay' : language === 'ta' ? 'Tamil' : language === 'th' ? 'Thai' : ''}{' '}
                language.
              </p>
            </div>
          </div>
        </div>
        {/* Cultural adaptation showcase */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-719">
          <div className="bg-white rounded-lg shadow-card p-6 hover:shadow-glow-mint transition-all duration-300" data-id="element-720">
            <h3 className="text-lg font-medium text-charcoal mb-4 flex items-center" data-id="element-721">
              <div className="w-8 h-8 rounded-full bg-sage-mint/20 flex items-center justify-center mr-3" data-id="element-722">
                <ArrowRight className="h-4 w-4 text-sage-mint" data-id="element-723" />
              </div>
              Cultural Adaptation
            </h3>
            <p className="text-grey-600 text-sm" data-id="element-724">
              Communication style adapts to cultural norms, showing respect for
              family involvement and hierarchical considerations important in{' '}
              {language === 'zh' ? 'Chinese' : language === 'ms' ? 'Malay' : language === 'ta' ? 'Indian' : language === 'th' ? 'Thai' : 'diverse'}{' '}
              culture.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-card p-6 hover:shadow-glow-mint transition-all duration-300" data-id="element-725">
            <h3 className="text-lg font-medium text-charcoal mb-4 flex items-center" data-id="element-726">
              <div className="w-8 h-8 rounded-full bg-sage-mint/20 flex items-center justify-center mr-3" data-id="element-727">
                <ArrowRight className="h-4 w-4 text-sage-mint" data-id="element-728" />
              </div>
              Professional Standards
            </h3>
            <p className="text-grey-600 text-sm" data-id="element-729">
              Maintains clinical precision and evidence-based approach while
              adapting to patient preferences and cultural context. Finnish
              healthcare standards applied with cultural sensitivity.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-card p-6 hover:shadow-glow-mint transition-all duration-300" data-id="element-730">
            <h3 className="text-lg font-medium text-charcoal mb-4 flex items-center" data-id="element-731">
              <div className="w-8 h-8 rounded-full bg-sage-mint/20 flex items-center justify-center mr-3" data-id="element-732">
                <ArrowRight className="h-4 w-4 text-sage-mint" data-id="element-733" />
              </div>
              Multilingual Advantage
            </h3>
            <p className="text-grey-600 text-sm" data-id="element-734">
              Ability to communicate in multiple languages enables deeper
              connection with patients from diverse backgrounds, reducing
              misunderstandings and improving care outcomes.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
export default PatientInteraction;